import {Button, ButtonGroup, Choice, closeCurrentDialog} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotEmpty} from 'ramda';

import {PurchasePriceRuleResponseBody} from '@dms/api/sales';
import {i18n} from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';

import {useNavigate} from 'shared/web';

import {PurchasePriceRuleNewLocationState} from '../../../../../types/PurchasePriceRuleNewLocationStateSchema';
import {
  buildPurchasePriceRuleTemplate,
  NewPurchasePriceRuleMode,
} from '../../../../../utils/buildPurchasePriceRuleTemplate';
import {NewPurchasePriceRuleOption} from './NewPurchasePriceRuleOption';

interface NewPurchasePriceRuleDialogProps {
  rules: PurchasePriceRuleResponseBody[];
}

export function NewPurchasePriceRuleDialog(props: NewPurchasePriceRuleDialogProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<NewPurchasePriceRuleMode>('COPY_DEFAULT');
  const [sourceRuleId, setSourceRuleId] = useState<string | null>(null);

  const sourceRuleOptions = props.rules
    .filter((rule) => !rule.system)
    .map((rule) => ({value: rule.id, label: rule.name}));

  const template = buildPurchasePriceRuleTemplate({mode, rules: props.rules, sourceRuleId});

  const handleContinue = () => {
    if (isNil(template)) {
      return;
    }

    const state: PurchasePriceRuleNewLocationState = {template};

    closeCurrentDialog();
    navigate(settingsRoutes.purchasePriceRuleNew, {state});
  };

  return (
    <VStack spacing={4}>
      <Text size="small">
        {i18n.t('page.settings.purchasePrice.marginRules.newRuleDialog.intro')}
      </Text>

      <NewPurchasePriceRuleOption
        title={i18n.t('page.settings.purchasePrice.marginRules.newRuleDialog.copyDefault.title')}
        description={i18n.t(
          'page.settings.purchasePrice.marginRules.newRuleDialog.copyDefault.description'
        )}
        isSelected={mode === 'COPY_DEFAULT'}
        onSelect={() => setMode('COPY_DEFAULT')}
        data-testid={testIds.settings.purchasePrice('newRuleCopyDefault')}
      />

      <Show when={isNotEmpty(sourceRuleOptions)}>
        <NewPurchasePriceRuleOption
          title={i18n.t('page.settings.purchasePrice.marginRules.newRuleDialog.copyOther.title')}
          description={i18n.t(
            'page.settings.purchasePrice.marginRules.newRuleDialog.copyOther.description'
          )}
          isSelected={mode === 'COPY_OTHER'}
          onSelect={() => setMode('COPY_OTHER')}
          data-testid={testIds.settings.purchasePrice('newRuleCopyOther')}
        >
          <Choice<string>
            value={sourceRuleId}
            onChange={setSourceRuleId}
            options={sourceRuleOptions}
            label={i18n.t('page.settings.purchasePrice.marginRules.newRuleDialog.sourceRule')}
            isDisabled={mode !== 'COPY_OTHER'}
            isRequired
            isNotClearable
            data-testid={testIds.settings.purchasePrice('newRuleSourceRule')}
          />
        </NewPurchasePriceRuleOption>
      </Show>

      <NewPurchasePriceRuleOption
        title={i18n.t(
          'page.settings.purchasePrice.marginRules.newRuleDialog.withoutModifiers.title'
        )}
        description={i18n.t(
          'page.settings.purchasePrice.marginRules.newRuleDialog.withoutModifiers.description'
        )}
        isSelected={mode === 'WITHOUT_MODIFIERS'}
        onSelect={() => setMode('WITHOUT_MODIFIERS')}
        data-testid={testIds.settings.purchasePrice('newRuleWithoutModifiers')}
      />

      <Text size="small" color="secondary">
        {i18n.t('page.settings.purchasePrice.marginRules.newRuleDialog.footerNote')}
      </Text>

      <ButtonGroup align="right">
        <Button
          variant="secondary"
          title={i18n.t('general.actions.cancel')}
          onClick={closeCurrentDialog}
          data-testid={testIds.settings.purchasePrice('newRuleCancel')}
        />
        <Button
          title={i18n.t('general.actions.continue')}
          isDisabled={isNil(template)}
          onClick={handleContinue}
          data-testid={testIds.settings.purchasePrice('newRuleContinue')}
        />
      </ButtonGroup>
    </VStack>
  );
}
